import type { components } from "./generated/openapi-types";

// The client for the inspector API, typed from the SAME OpenAPI contract the
// service is built from — so a change to the spec breaks this file at compile
// time rather than at runtime in front of an operator.

export type Direction = components["schemas"]["Direction"];
export type Mailbox = components["schemas"]["Mailbox"];
export type MailboxInfo = components["schemas"]["MailboxInfo"];
export type FileSummary = components["schemas"]["FileSummary"];
export type FileList = components["schemas"]["FileList"];
export type FileDetail = components["schemas"]["FileDetail"];
export type DecodedLine = components["schemas"]["Line"];
export type DecodedField = components["schemas"]["Field"];
export type InspectorConfig = components["schemas"]["InspectorConfig"];
export type ContentKind = components["schemas"]["ContentKind"];
export type Guideline = components["schemas"]["Guideline"];
export type GuidelineGroup = components["schemas"]["GuidelineGroup"];
export type Poller = components["schemas"]["Poller"];
export type PollRun = components["schemas"]["PollRun"];
export type DeployedFunction = components["schemas"]["DeployedFunction"];
export type FunctionRole = components["schemas"]["FunctionRole"];
export type LogWindow = components["schemas"]["LogWindow"];
export type LogLine = components["schemas"]["LogLine"];
export type LogTail = components["schemas"]["LogTail"];
export type InspectorUser = components["schemas"]["InspectorUser"];
export type UserStatus = components["schemas"]["UserStatus"];
export type DeliveryBoard = components["schemas"]["DeliveryBoard"];
export type DeliveryRow = components["schemas"]["DeliveryRow"];
export type DeliveryStatus = components["schemas"]["DeliveryStatus"];
export type Answer = components["schemas"]["Answer"];
export type UnpairedAnswer = components["schemas"]["UnpairedAnswer"];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface Problem {
  title?: string;
  detail?: string;
}

async function call(
  path: string,
  token: string | undefined,
  method: "GET" | "POST" | "DELETE" = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  const response = await fetch(`/api${path}`, {
    method,
    ...(signal ? { signal } : {}),
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const problem = (await response.json()) as Problem;
      message = problem.detail ?? problem.title ?? message;
    } catch {
      // A non-JSON error body (an API Gateway rejection) leaves the default.
    }
    throw new ApiError(response.status, message);
  }
  return response;
}

const request = async <T,>(path: string, token: string | undefined): Promise<T> =>
  (await (await call(path, token)).json()) as T;

/** Public Cognito parameters. The one call made before a user has signed in. */
export const fetchConfig = (): Promise<InspectorConfig> =>
  request<InspectorConfig>("/v1/config", undefined);

/**
 * How a mailbox is named on screen: by the subsystem the bank knows it by, with
 * the folder id beside it when the two differ (a test agreement's `KR9` in the
 * `bs1` folder).
 */
export const mailboxLabel = (info: MailboxInfo): string =>
  info.subsystem.toUpperCase() === info.id.toUpperCase() ? info.subsystem : `${info.subsystem} · ${info.id}`;

export function listFiles(
  token: string,
  mailbox: Mailbox,
  direction: Direction,
  options: { q?: string; cursor?: string; limit?: number } = {},
): Promise<FileList> {
  const params = new URLSearchParams({ mailbox, direction });
  if (options.q) params.set("q", options.q);
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.limit) params.set("limit", String(options.limit));
  return request<FileList>(`/v1/files?${params.toString()}`, token);
}

export const getFile = (token: string, mailbox: Mailbox, direction: Direction, name: string): Promise<FileDetail> =>
  request<FileDetail>(`/v1/files/${mailbox}/${direction}/${encodeURIComponent(name)}`, token);

/** One mailbox's deliveries of the last `days` days, each with the bank's receipts for it. */
export const listDeliveries = async (token: string, mailbox: Mailbox, days: number, signal?: AbortSignal): Promise<DeliveryBoard> =>
  (await (await call(`/v1/deliveries?${new URLSearchParams({ mailbox, days: String(days) }).toString()}`, token, "GET", undefined, signal)).json()) as DeliveryBoard;

/** The bank's own documentation, as shipped with this deployment. */
export const listGuidelines = async (token: string): Promise<Guideline[]> =>
  (await request<{ guidelines: Guideline[] }>("/v1/guidelines", token)).guidelines;

/** One guideline PDF. Fetched with the token, for the reason below. */
export const fetchGuideline = async (token: string, slug: string): Promise<Blob> =>
  await (await call(`/v1/guidelines/${encodeURIComponent(slug)}`, token)).blob();

/** The SFTP pollers, one per mailbox — the administration board's listing. */
export const listPollers = async (token: string): Promise<Poller[]> =>
  (await request<{ pollers: Poller[] }>("/v1/admin/pollers", token)).pollers;

/**
 * Runs a mailbox's poller now. The one call on this API that is not a read:
 * what the hourly schedule does, done on request. Resolves with what the poll
 * did — or that it is still running (a 202), in which case the files will show
 * up in the listing as they land. A collision with a poll already running is a
 * 409 and arrives here as an ApiError.
 */
export const runPoller = async (token: string, mailbox: Mailbox): Promise<PollRun> =>
  (await (await call(`/v1/admin/pollers/${mailbox}/runs`, token, "POST")).json()) as PollRun;

/** The service's functions, whose logs the board can show. */
export const listFunctions = async (token: string): Promise<DeployedFunction[]> =>
  (await request<{ functions: DeployedFunction[] }>("/v1/admin/functions", token)).functions;

/**
 * One page of the lines a function wrote within the window, newest first. With
 * a `cursor` (the previous page's `nextCursor`), the page of older lines below it.
 */
export function getFunctionLogs(
  token: string,
  fn: string,
  options: { window: LogWindow; limit?: number; cursor?: string },
): Promise<LogTail> {
  const params = new URLSearchParams({ window: options.window });
  if (options.limit) params.set("limit", String(options.limit));
  if (options.cursor) params.set("cursor", options.cursor);
  return request<LogTail>(`/v1/admin/functions/${encodeURIComponent(fn)}/logs?${params.toString()}`, token);
}

/** Who may sign in here — the inspector's own user pool. */
export const listUsers = async (token: string): Promise<InspectorUser[]> =>
  (await request<{ users: InspectorUser[] }>("/v1/admin/users", token)).users;

/** Creates an account for the address; Cognito emails the invitation. */
export const createUser = async (token: string, email: string): Promise<InspectorUser> =>
  (await (await call("/v1/admin/users", token, "POST", { email })).json()) as InspectorUser;

/** Removes an account. Access ends at once. */
export const deleteUser = async (token: string, username: string): Promise<void> => {
  await call(`/v1/admin/users/${encodeURIComponent(username)}`, token, "DELETE");
};

/**
 * The file's original bytes.
 *
 * Deliberately NOT a plain `<a href>` to the raw path: the API Gateway JWT
 * authorizer reads the `Authorization` header, and a browser navigation cannot
 * carry one — a link download comes back "Unauthorized". So the bytes are
 * fetched like every other call and handed to the page as a blob to save.
 */
export const fetchRawFile = async (token: string, mailbox: Mailbox, direction: Direction, name: string): Promise<Blob> =>
  await (await call(`/v1/files/${mailbox}/${direction}/${encodeURIComponent(name)}/raw`, token)).blob();
